import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Info } from "lucide-react";

import type { SectionEnvelope } from "@/api/diagnostic";
import { Pill } from "@/components/brand/Pill";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCurrentUser } from "@/hooks/useAuth";
import { formatDate, formatDateTime } from "@/lib/datetime";
import { formatCount } from "@/lib/format";
import { COMMERCIAL_CALIBRATORS } from "@/lib/roles";
import { cn } from "@/lib/utils";
import { StackedBar } from "../primitives/StackedBar";
import {
  DiagnosticSection,
  Section,
  type DiagnosticTabProps,
} from "../DiagnosticSection";
import { leverLabel } from "../diagnosticMeta";
import {
  bomReviewCaveat,
  formatBrl,
  includedInAllowance,
  investmentComposition,
  digitalAccessConsumption,
  licenseLines,
  licenseTotals,
  licensingComposition,
  numberProvenance,
  outOfScopeExcluded,
  projectTimeline,
  type ProjectTimeline,
  type Provenance,
  type LicensingComposition,
  type MetricConsumption,
  type Metric,
} from "../diagnosticData";
import { NO_VALUE, readSection } from "../sectionState";
import { awaitingPricingAppBom, currentBom, type CurrentBom } from "../data/currentBom";
import { commercialOffer, type CommercialOffer } from "../data/commercialOffer";
import { formatDiscount } from "../bom/discountPercent";
import { PHASE_BAR, missingMessage, phasePercents } from "./investmentPhases";

/**
 * Investimento — a aba 5 do Diagnóstico (DIAG.7 / #468).
 *
 * **Apresentacional por contrato**: recebe `sections` e `snapshots` por props e
 * não chama hook de dado — testável sem mock, no molde das quatro abas
 *
 * A primeira aba do Diagnóstico que **não pede backend** (S-066): o
 * `_COST_FIELDS` do DIAG.1 já trazia os 10 campos do `cost_breakdown` inteiros
 * porque a `EffortTab` do Architect já os consumia todos. Nenhuma conta é
 * refeita aqui — tudo vem pronto de `diagnosticData.ts`.
 *
 * O valor do projeto é o cenário marcado na Calibragem comercial (EF-FUNC-012),
 * do domínio `validation.commercial_offer`. A estimativa de desenvolvimento do
 * Architect (`cost_breakdown`) não aparece mais aqui.
 *
 * Sem card de cronograma: nenhuma das duas fontes de calendário do repo
 * (`target_go_live_date` em texto livre, `total_duration_weeks` gerado por LLM)
 * trafega no snapshot do diagnóstico.
 */
export function InvestmentAct({
  sections,
  snapshots,
  calibration,
  validation,
}: DiagnosticTabProps) {
  // #1345: com a BOM do Pricing App aplicada, o software do ato é o dela, lido
  // ao vivo do bloco `validation`; sem ela, segue a calculada de `bom_totals`.
  const bom = currentBom(validation);
  return (
    <div className="grid grid-cols-1 gap-4">
      <CompositionCard
        sections={sections}
        snapshots={snapshots}
        calibration={calibration}
        validation={validation}
      />
      {bom && validation?.bom_lines && (
        <CurrentBomCard
          bom={bom}
          envelope={validation.bom_lines}
          publishedAt={calibration?.published_at ?? undefined}
        />
      )}
      <LicenseTableCard sections={sections} snapshots={snapshots} />
      <MetricSourceCard sections={sections} snapshots={snapshots} />
      <OutOfScopeCaveat sections={sections} />
    </div>
  );
}

/**
 * Ressalva de leitura, como **texto** do card — nunca `title=`.
 *
 * `tone="warning"` é a mesma forma no ocre que o `IndicativeValue` já usa para
 * "esse número não é firme" (#753). Vale para a ressalva que muda o valor, não
 * para a que só o explica — se todas gritarem, nenhuma é lida.
 */
function Caveat({
  testId = "investment-caveat",
  tone = "neutral",
  title,
  children,
}: {
  testId?: string;
  tone?: "neutral" | "warning";
  /** Prosa pt-BR vinda do backend. Política da casca: nunca como texto principal. */
  title?: string;
  children: ReactNode;
}) {
  const warning = tone === "warning";
  const Icon = warning ? AlertTriangle : Info;

  return (
    <p
      data-testid={testId}
      data-tone={tone}
      title={title}
      className={cn(
        "mb-3 flex items-start gap-2 rounded-md border px-3 py-2 text-[11px] leading-snug",
        warning
          ? "border-[#e7d3a3] bg-[#fdf8ec] text-[#7a5600]"
          : "border-[#e8ebf0] bg-[#f7f9fc] text-[#596b85]",
      )}
    >
      <Icon
        aria-hidden
        className={cn(
          "mt-px h-3.5 w-3.5 shrink-0",
          warning ? "text-[#b57a00]" : "text-[#7b8799]",
        )}
      />
      <span>{children}</span>
    </p>
  );
}

const OVERLINE =
  "font-mono text-[10px] uppercase tracking-[0.14em] text-[#7b8799]";

/* -------------------------------------------------------------------------- */
/* 1 · composição                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Os números de `bom_totals` (ou da BOM vigente), o valor do projeto da
 * Calibragem comercial e o prazo da Calibragem técnica.
 *
 * `showsSampling={false}`: as células de software leem agregados sobre a população
 * inteira — herdar "mostrando 200 de 340" afirmaria que o total mensal saiu da
 * amostra de linhas, quando saiu do agregado pronto.
 */
function CompositionCard({
  sections,
  snapshots,
  calibration,
  validation,
}: DiagnosticTabProps) {
  const { t } = useTranslation();
  const calculated = investmentComposition(sections);
  // A BOM vigente do Pricing App troca o software (anual, mensal, linhas e
  // usuários); o projeto e o prazo continuam vindo da Calibragem.
  const bom = currentBom(validation);
  // #1714, cartão 6.5: sem a BOM do Pricing App, o software espera por ela.
  const awaiting = !bom && awaitingPricingAppBom(validation);
  const merged = bom ? { ...calculated, ...pricingAppComposition(bom) } : calculated;
  const composition = awaiting
    ? { ...merged, monthly: { value: undefined, text: NO_VALUE } }
    : merged;
  // #753: a ressalva do BOM fica JUNTO dos números, não numa aba de detalhe.
  // Um BOM pendente de revisão tinha a mesma aparência de um aprovado, e numa
  // demonstração ao vivo isso não aparece como lacuna — aparece como número
  // redondo.
  // A ressalva é da BOM calculada; com a do Pricing App vigente, não se aplica.
  const bomCaveat = bom || awaiting ? undefined : bomReviewCaveat(sections);
  // #822: a ressalva vale sempre que o faturável deixa de ser gente, e não só
  // quando há Digital Access.
  //
  // Medido na execução `8dbdde7d`: 2 pessoas viraram 55 faturáveis porque o
  // 8019322 tem mínimo de 25 blocos e o 8019763 tem 30 — `55 = 25 + 30` é soma
  // de mínimos contratuais. A flag `is_digital_access` era nula nas duas
  // execuções medidas, então a ressalva certa nunca aparecia, e 55 saía na tela
  // parecendo contagem de pessoas, com inflação de 27x.
  //
  // A condição estrutural SOMA-SE à flag, e não a substitui.
  //
  // `effective_count !== user_count` cobre mínimo de SKU, que é o caso medido na
  // `8dbdde7d`. Não cobre Digital Access: o `resolve_skus.py:1068` grava a linha
  // de DA com `user_count == effective_count == blocks`, então o predicado é
  // falso nela e um projeto cujo único desvio é DA perderia a ressalva inteira —
  // que é o comportamento anterior à #822, de volta.
  //
  // `userCount !== undefined` em vez de `?? 0`: "não sei quantas pessoas" não é
  // "zero pessoas", e trocar um pelo outro é a substituição que esta frente
  // inteira existe para remover.
  const lines = licenseLines(sections);
  const hasDigitalAccess = lines.some((line) => line.isDigitalAccess);
  const billableIsNotHeadcount = !bom && lines.some(
    (line) =>
      line.isDigitalAccess ||
      (line.effectiveCount !== undefined &&
        line.userCount !== undefined &&
        line.effectiveCount !== line.userCount),
  );

  // EF-FUNC-012 v0.3: o valor do projeto é o cenário marcado na Calibragem
  // comercial, e a origem do ato passa a ser ela ("calibragem_comercial ·
  // cenário selecionado"), com o software ainda vindo da BOM vigente.
  const offer = commercialOffer(validation);
  const offerSource = validation?.commercial_offer?.source;
  const source = offerSource
    ? { ...offerSource, ref: bom ? `${offerSource.ref} · software: bom_lines` : offerSource.ref }
    : bom
      ? validation?.bom_lines?.source
      : undefined;

  // #877: prazo e procedência vêm do bloco de CALIBRAGEM, não do `bom_totals`.
  // Sendo filhos da seção, eles sumiam junto com ela: sem BOM coletado, o
  // `DiagnosticSection` troca as crianças pelo corpo fechado, e um projeto
  // calibrado perdia o prazo e o selo enquanto o título seguia prometendo
  // "Investment and timeline". Fora dela, cada um responde pela própria origem.
  const bomEnvelope = (sections as Record<string, SectionEnvelope | undefined>)
    .bom_totals;
  const bomShowsData = bomEnvelope ? readSection(bomEnvelope).showsData : false;

  return (
    <>
      <Section
        sections={sections}
        name="bom_totals"
        title={t("Investment and timeline")}
        snapshots={snapshots}
        showsSampling={false}
        source={source}
      >
        {bomCaveat && (
          <Caveat testId="bom-review-caveat" tone="warning">
            {t(
              "This BOM is pending review — the totals below may change once the pending decisions are confirmed.",
            )}
          </Caveat>
        )}
        {/* Os dois números que o cliente veio ouvir (o software por ano e o
          projeto) vêm grandes; mensal, linhas e faturável são o detalhe que
          sustenta os dois. Antes as cinco celulas tinham o mesmo peso, e o
          ato da revelação abria sem revelação nenhuma. */}
        <ProvenanceSeal provenance={numberProvenance(calibration, snapshots)} />
        {/* MOC v3: software, projeto e prazo em 1.1 : 1.15 : 0.75. O card do
            projeto é o mais largo, e os valores dele cabem numa linha. */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.1fr_1.15fr_0.75fr]">
          {bom ? (
            <SoftwareCell bom={bom} />
          ) : awaiting ? (
            <CompositionCell
              testId="annual"
              label={t("SAP GROW software · recurring")}
              metric={{ value: undefined, text: t("Awaiting the Pricing App BOM") }}
              hero="primary"
              support={t("The software value comes from the Pricing App BOM, loaded in node 06.")}
            />
          ) : (
            <CompositionCell
              testId="annual"
              label={t("SAP GROW software · recurring")}
              metric={composition.annual}
              hero="primary"
              support={t("{{lines}} SKU lines · {{units}} units billed", {
                lines: composition.skuLines.text,
                units: composition.billableCount.text,
              })}
            />
          )}
          <ProjectOfferCell offer={offer} />
          <TimelineCell timeline={projectTimeline(calibration)} />
        </div>
        <div className="mt-5 grid grid-cols-3 gap-4 border-t border-[#e6ecf4] pt-4">
          <CompositionCell
            testId="monthly"
            label={t("Monthly")}
            metric={composition.monthly}
          />
          <CompositionCell
            testId="sku-lines"
            label={t("SKU lines")}
            metric={composition.skuLines}
          />
          <CompositionCell
            testId="billable-count"
            label={bom ? t("Licensed users") : t("Billable units")}
            metric={composition.billableCount}
          />
        </div>
        {billableIsNotHeadcount && (
          <div className="mt-4">
            <Caveat testId="investment-composition-caveat">
              {hasDigitalAccess
                ? t(
                    "The billable count is not a headcount — it includes SKU contractual minimums and Digital Access document blocks.",
                  )
                : t(
                    "The billable count is not a headcount — where a SKU's contractual minimum exceeds the number of people, the minimum is what is billed.",
                  )}
            </Caveat>
          </div>
        )}
      </Section>
      {/* Sem BOM coletado a seção acima fecha, e com ela iriam o prazo e o selo,
        que não dependem dela. Aqui eles seguem, no próprio card. */}
      {!bomShowsData && (
        <div
          data-testid="investment-timeline-standalone"
          className="rounded-lg border border-[#e8ebf0] bg-white p-5"
        >
          {/* Título PRÓPRIO: a seção acima segue imprimindo o dela mesmo
              fechada (o `DiagnosticSection` só troca as crianças), e repetir
              "Investment and timeline" punha dois cabeçalhos iguais em
              sequência. Este card carrega o prazo, então é isso que ele
              anuncia. */}
          <h3 className="text-sm font-semibold text-[#08254c]">
            {t("Project timeline")}
          </h3>
          <ProvenanceSeal
            provenance={numberProvenance(calibration, snapshots)}
          />
          {/* `bare`: este `div` já é o card. Sem isso são duas bordas e 40px
              de padding, uma dentro da outra. */}
          <TimelineCell timeline={projectTimeline(calibration)} bare />
        </div>
      )}
    </>
  );
}

/**
 * O card "Projeto de migração" (EF-FUNC-012 v0.3 RF-02 a RF-05, MOC v3).
 *
 * O valor ofertado ao cliente em destaque e, abaixo, o valor original do projeto,
 * o Incentivo Exed e o Funding Hyperscale, este numa linha própria mesmo zerado.
 * Sem legenda de cenário, margem ou versão: o cliente não vê nada disso. A
 * composição "(C&S + A&O)" aparece só para quem faz a calibragem comercial.
 */
function ProjectOfferCell({ offer }: { offer: CommercialOffer }) {
  const { t } = useTranslation();
  const role = useCurrentUser()?.role ?? "";
  const showComposition = (COMMERCIAL_CALIBRATORS as readonly string[]).includes(role);
  const value = "shrink-0 whitespace-nowrap font-mono text-[13px] font-semibold tabular-nums";

  return (
    <div
      data-testid="investment-project-offer"
      className="rounded-lg border-2 border-[#ffc83d] bg-[#fffdf5] p-5"
    >
      <div className={OVERLINE}>{t("Migration project · one-off")}</div>
      {offer.state === "ready" ? (
        <>
          <div className={cn(OVERLINE, "mt-2")}>{t("Offered value to the client")}</div>
          <div
            data-testid="project-offered-value"
            className="mt-1 text-3xl font-extrabold tabular-nums text-[#08254c]"
          >
            {formatBrl(offer.offered)}
          </div>
          <div className="mt-3 flex flex-col gap-0.5 border-t border-[#e8ebf0] pt-2 text-[13px]">
            <div className="flex items-center justify-between gap-3 py-0.5">
              <span className="text-[#596b85]">{t("Original project value")}</span>
              <span data-testid="project-original-value" className={value}>
                {formatBrl(offer.original)}
              </span>
            </div>
            <div
              data-testid="project-exed-incentive-row"
              className="flex items-center justify-between gap-3 py-0.5"
            >
              <span className="text-[#596b85]">
                {t("Exed incentive")}
                {showComposition ? (
                  <span className="ml-1 text-[#9aa6bb]">{t("(C&S + A&O)")}</span>
                ) : null}
              </span>
              <span data-testid="project-exed-incentive" className={cn(value, "text-[#15803d]")}>
                − {formatBrl(offer.exedIncentive)}
              </span>
            </div>
            <div className="-mx-1.5 flex items-center justify-between gap-3 rounded-md bg-[#f3e8ff] px-1.5 py-0.5">
              <span className="font-semibold text-[#6b21a8]">{t("Hyperscale funding")}</span>
              <span
                data-testid="project-hyperscale-funding"
                className={cn(value, "text-[#9333ea]")}
              >
                − {formatBrl(offer.hyperscaleFunding)}
              </span>
            </div>
          </div>
        </>
      ) : (
        <p className="mt-2 text-lg font-semibold text-[#7b8799]">
          {offer.state === "awaiting"
            ? t("Awaiting commercial calibration")
            : t("The commercial calibration could not be read.")}
        </p>
      )}
    </div>
  );
}

/** O terceiro card de topo: o prazo, particionado pelas cinco fases (#877).
 *
 * O prazo só existe com calibragem publicada, porque quem o calcula é o motor
 * de esforço. Sem ela o card diz isso, e não mostra zero: ausência declarada
 * em vez de medição inventada.
 */
function TimelineCell({
  timeline,
  bare = false,
}: {
  timeline: ProjectTimeline | null;
  /** Sem moldura própria, para quando quem chama já é o card. */
  bare?: boolean;
}) {
  const { t } = useTranslation();
  // Fechados em 100 uma vez só: a barra e a legenda leem a mesma lista.
  const percents = timeline?.phases ? phasePercents(timeline.phases) : [];
  const labels: Record<string, string> = {
    explore_hh: t("Explore"),
    config_hh: t("Configure"),
    test_hh: t("Test"),
    deploy_hh: t("Deploy"),
    hypercare_hh: t("Hypercare"),
    overhead_hh: t("Overhead and contingency"),
  };

  return (
    <div
      data-testid="investment-timeline"
      className={cn(!bare && "rounded-lg border border-[#e8ebf0] p-5")}
    >
      {/* Some com `bare` porque aí quem chama já imprime o título, e os dois
          usam a MESMA chave. Esta guarda já existiu e se perdeu numa
          reescrita do bloco dentro desta PR: o conserto do título duplicado
          produziu um título duplicado. Por isso há teste abaixo contando
          quantas vezes o texto aparece. */}
      {!bare && <div className={OVERLINE}>{t("Project timeline")}</div>}
      {timeline === null ? (
        <p className="mt-2 text-sm text-[#5b6b82]">
          {t(
            "Not calibrated yet — the timeline comes from the calibration step.",
          )}
        </p>
      ) : (
        <>
          {timeline.months === null ? (
            /* Duração ausente ou zero. O motor devolve `0.0` por fallback de
               divisão quando a equipe é zero, e "0.0 meses" ao lado de uma
               partição completa tem cara de medido. */
            <p className="mt-2 text-sm text-[#5b6b82]">
              {t(
                "This published version does not carry the project duration — calibrate again to see it.",
              )}
            </p>
          ) : (
            <div className="mt-1 text-3xl font-semibold tabular-nums text-[#08254c]">
              {t("{{months}} months", {
                months: formatCount(timeline.months, { decimals: 1 }),
              })}
            </div>
          )}
          {timeline.phases === null ? (
            /* A versão publicada não carrega a partição. Isso não é hipótese:
               até a #877 o `PreviewOut` expunha só o `config_hh`, e toda
               calibragem publicada antes dela tem o `numbers` incompleto.
               Repartir o que sobrou afirmaria que Configurar é 100% do
               esforço e que as outras quatro custam zero hora.

               A mensagem é UMA, escolhida pelo motivo que o seletor declara.
               Antes ela era escolhida por ternário sobre o que estava nulo, e
               com a duração faltando saíam DUAS: a da duração e, logo abaixo,
               "não carrega o esforço por fase" — falsa, porque as cinco fases
               estavam no payload. Dizer a mensagem errada manda o consultor
               procurar no lugar errado, que foi o achado da rodada anterior,
               um ramo adiante. */
            /* A escolha fica FORA do `<p>`: com `missing === "duration"` a
               mensagem já saiu acima, e manter o parágrafo deixava um nó
               vazio com 8px de folga e um alvo mudo para leitor de tela. */
            missingMessage(timeline.missing, t) && (
              <p className="mt-2 text-sm text-[#5b6b82]">
                {missingMessage(timeline.missing, t)}
              </p>
            )
          ) : (
            <>
              {/* Uma barra, e não cinco linhas: com cinco, o card ficava o
                  dobro da altura dos dois vizinhos e esticava a grade inteira.
                  O empilhado com legenda é o vocabulário que o Ato 1 e o Ato 2
                  já usam.

                  O número grande é DURAÇÃO e a barra é ESFORÇO: grandezas
                  diferentes a três centímetros uma da outra, então a legenda
                  diz qual é qual em vez de deixar o leitor supor. */}
              {/* A barra reparte o esforço TOTAL, que é o mesmo número que
                  produz os meses acima: repartir só entre as cinco fases faria
                  cada uma parecer mais que o dobro do que é. O total em HH saiu
                  da legenda pela EF-DIAG-003 (#1773); "Overhead e contingência"
                  fecha os 100%. */}
              <div className="mt-3 text-xs text-[#5b6b82]">{t("Effort by phase")}</div>
              <StackedBar
                className="mt-1.5"
                height="h-2"
                segments={timeline.phases.map((phase, index) => ({
                  key: phase.key,
                  /* O `aria-label` e o `title` do empilhado concatenam
                     `${value} ${label}`. Com o percentual como valor, o leitor
                     de tela e a dica do mouse dizem "8 % Explore", o mesmo da
                     legenda visível, e nenhum dos dois mostra HH
                     (EF-DIAG-003, #1773). */
                  label: `% ${labels[phase.key]}`,
                  className: PHASE_BAR[phase.key] ?? "bg-[#e8ebf0]",
                  //: Fechado em 100, como na legenda. A fatia é proporcional ao
                  //: percentual, que é a mesma proporção das horas.
                  value: percents[index],
                }))}
              />
              {/* A legenda é própria, e não a do `StackedBar`, porque a dele
                  mostra o valor cru sem unidade. Mostra só o percentual: as HH
                  ficam na Calibragem técnica (EF-DIAG-003, #1773). */}
              <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#5b6b82]">
                {timeline.phases.map((phase, index) => (
                  <li key={phase.key} className="flex items-center gap-1.5">
                    <span
                      aria-hidden
                      className={cn(
                        "size-2 shrink-0 rounded-sm",
                        PHASE_BAR[phase.key] ?? "bg-[#e8ebf0]",
                      )}
                    />
                    <span>{labels[phase.key]}</span>
                    <span className="tabular-nums">{percents[index]}%</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  );
}

/** De onde vêm os números deste ato, dito na cara (#877).
 *
 * O critério da issue é que o Diagnóstico declare a procedência, e não só
 * acerte o número. "Calibrado" sem autor não responde a pergunta seguinte,
 * que é com quem falar sobre ele.
 */
function ProvenanceSeal({ provenance }: { provenance: Provenance }) {
  const { t } = useTranslation();
  // `formatDate` da casa, e não `toLocaleDateString()` cru: o cru usa o locale
  // do NAVEGADOR, então um consultor pt-BR num Chrome en-US veria "9/28/2026"
  // num Diagnóstico em português, que é o artefato que vai ao cliente. E ele
  // devolve "—" em data inválida, em vez de imprimir "Invalid Date" no selo.
  const when = provenance.at ? formatDate(provenance.at) : null;

  const text =
    provenance.kind === "calibrated"
      ? provenance.by
        ? t("Calibrated by {{who}}{{when}}", {
            who: provenance.by,
            when: when ? ` · ${when}` : "",
          })
        : // Sem autor (usuário removido: `published_by` não tem FK), mas a
          // data é conhecida e responde metade da pergunta. Descartá-la junto
          // seria jogar fora o que se sabe.
          t("Calibrated{{when}}", { when: when ? ` · ${when}` : "" })
      : provenance.kind === "approved"
        ? t("Approved scope{{when}}", { when: when ? ` · ${when}` : "" })
        : provenance.kind === "machine"
          ? t("From the agents — nobody has calibrated these numbers yet.")
          : t("Origin not read.");

  return (
    <p
      data-testid="investment-provenance"
      className="mb-3 text-xs text-[#5b6b82]"
    >
      {text}
    </p>
  );
}

/**
 * EF-FUNC-010 RF-06 (#1621): o software com desconto da BOM vigente, com TCV e
 * ACV lado a lado e do mesmo tamanho. Antes o TCV vinha numa linha pequena, e é
 * ele o valor do contrato. Abaixo, a lista e o desconto por ano e a versão. O
 * selo diz que alguma linha do total saiu do desconto do Pricing App.
 */
function SoftwareCell({ bom }: { bom: CurrentBom }) {
  const { t } = useTranslation();
  const months = bom.periodMonths;
  const listPerYear = months ? (bom.totalList * 12) / months : bom.totalList;
  const adjusted = bom.lines.some(
    (line) =>
      line.inTotal &&
      line.discount !== undefined &&
      line.effectiveDiscount !== undefined &&
      line.effectiveDiscount !== line.discount,
  );
  const value = "mt-1 text-2xl font-semibold tabular-nums text-[#ffc83d]";
  const label = cn(OVERLINE, "text-[#ffc83d]");
  const note = "mt-1 text-[11px] text-[#c6d3e2]";
  return (
    <div
      data-testid="investment-composition-annual"
      className="relative rounded-lg bg-[#08254c] p-5"
    >
      {adjusted && (
        <span
          data-testid="investment-software-discount-adjusted"
          className="absolute -top-2 right-3 rounded-full bg-[#ffc83d] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#08254c]"
        >
          {t("Discount adjusted")}
        </span>
      )}
      <div className={cn(OVERLINE, "text-[#9db4d4]")}>
        {t("SAP GROW software · recurring · with discount")}
      </div>
      <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div data-testid="investment-software-tcv" className="min-w-0">
          <div className={label}>{t("TCV · contract total")}</div>
          <div data-testid="investment-software-value" className={value}>
            {formatBrl(bom.tcv)}
          </div>
          {months !== undefined && (
            <p className={note}>
              {t("{{months}} months ({{years}} years)", {
                months: formatCount(months),
                years: formatCount(months / 12, { decimals: months % 12 ? 1 : 0 }),
              })}
            </p>
          )}
        </div>
        <div data-testid="investment-software-acv" className="min-w-0">
          <div className={label}>{t("ACV · annual value")}</div>
          <div data-testid="investment-software-value" className={value}>
            {formatBrl(bom.acv)}
          </div>
          <p className={note}>{t("per year")}</p>
        </div>
      </div>
      <p
        data-testid="investment-software-detail"
        className="mt-3 border-t border-white/20 pt-2 text-[11px] leading-snug text-[#c6d3e2]"
      >
        {t("List price {{list}}/year · discount {{discount}}/year ({{percent}}) · {{version}}", {
          list: formatBrl(listPerYear),
          discount: formatBrl(listPerYear - bom.acv),
          percent: formatDiscount(bom.discountPct / 100),
          version:
            bom.source === "diagnostic_adjustment"
              ? t("Pricing App BOM adjusted in the Diagnostic (v{{version}})", {
                  version: bom.version,
                })
              : t("Pricing App BOM v{{version}}", { version: bom.version }),
        })}
      </p>
    </div>
  );
}

/** Os quatro números de software do card, pela BOM vigente do Pricing App. */
function pricingAppComposition(bom: CurrentBom) {
  const metric = (value: number, text: string): Metric => ({ value, text });
  return {
    annual: metric(bom.acv, formatBrl(bom.acv)),
    monthly: metric(bom.acv / 12, formatBrl(bom.acv / 12)),
    skuLines: metric(bom.skuLines, formatCount(bom.skuLines)),
    billableCount: metric(bom.licensedUsers, formatCount(bom.licensedUsers)),
  };
}

/**
 * As linhas da BOM vigente (#1345), como o consultor montou no Pricing App. A
 * linha que só o myGROW tem aparece no fim, marcada, e não soma: o que a
 * ferramenta apontou continua à vista sem mudar o contrato.
 *
 * Só quantidades (#1480). Na reunião de 19/10/2026 o Marco lembrou que o preço
 * de lista da SAP não pode ir para a tela do cliente, e o Diagnóstico é essa
 * tela. Preço, desconto, líquido e o ajuste ficam no nó da BOM do Functional.
 */
function CurrentBomCard({
  bom,
  envelope,
  publishedAt,
}: {
  bom: CurrentBom;
  envelope: SectionEnvelope;
  /** Publicação da Calibragem: com ela, projeto e prazo ficam congelados e a BOM não. */
  publishedAt?: string;
}) {
  const { t } = useTranslation();
  // #1345, critério 10: a BOM é lida ao vivo mesmo com a Calibragem publicada,
  // e uma versão salva depois dela muda o software sem que o resto do ato mude.
  const savedAfterCalibration =
    !!publishedAt &&
    !!bom.createdAt &&
    Date.parse(bom.createdAt) > Date.parse(publishedAt);
  return (
    <DiagnosticSection
      envelope={envelope}
      title={t("Pricing App BOM")}
      subtitle={t("Version {{version}} · current BOM of the project · quantity per line", {
        version: bom.version,
      })}
      showsSampling={false}
    >
      {savedAfterCalibration && (
        <div className="mt-3">
          <Caveat testId="bom-after-calibration">
            {t(
              "BOM version {{version}} was saved on {{saved}}, after the Calibration was published on {{published}}. The software numbers come from this version; project and timeline remain the published Calibration's.",
              {
                version: bom.version,
                saved: formatDateTime(bom.createdAt),
                published: formatDateTime(publishedAt),
              },
            )}
          </Caveat>
        </div>
      )}
      <Table data-testid="current-bom-table" className="mt-3">
        <TableHeader>
          <TableRow>
            <TableHead>{t("SKU")}</TableHead>
            <TableHead>{t("Description")}</TableHead>
            <TableHead>{t("Metric")}</TableHead>
            <TableHead className="text-right">{t("Quantity")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {bom.lines.map((line) => (
            <TableRow
              key={line.skuNumber}
              data-testid={`current-bom-row-${line.skuNumber}`}
              className={cn(!line.inTotal && "text-[#7b8799]")}
            >
              <TableCell className="font-mono text-xs">{line.skuNumber}</TableCell>
              <TableCell>
                {line.description}
                {!line.inTotal && (
                  <span className="ml-2 text-[11px]">
                    {t("Only in myGROW · not in total")}
                  </span>
                )}
              </TableCell>
              <TableCell className="text-xs">{line.metric ?? NO_VALUE}</TableCell>
              <TableCell className="text-right tabular-nums">
                {line.quantity == null ? NO_VALUE : formatCount(line.quantity)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </DiagnosticSection>
  );
}

function CompositionCell({
  testId,
  label,
  metric,
  hero,
  support,
}: {
  testId: string;
  label: string;
  metric: Metric;
  /** `primary` é o número que o ato existe para revelar: fundo navy, valor ouro. */
  hero?: "primary" | "secondary";
  support?: string;
}) {
  return (
    <div
      data-testid={`investment-composition-${testId}`}
      className={cn(
        hero === "primary" && "rounded-lg bg-[#08254c] p-5",
        hero === "secondary" && "rounded-lg border border-[#e8ebf0] p-5",
      )}
    >
      <div className={cn(OVERLINE, hero === "primary" && "text-[#9db4d4]")}>
        {label}
      </div>
      <div
        className={cn(
          "mt-1 font-semibold tabular-nums",
          hero ? "text-3xl" : "text-xl",
          hero === "primary" ? "text-[#ffc83d]" : "text-[#08254c]",
        )}
      >
        {metric.text}
      </div>
      {support && (
        <p
          className={cn(
            "mt-2 text-[11px] leading-snug",
            hero === "primary" ? "text-[#c6d3e2]" : "text-[#596b85]",
          )}
        >
          {support}
        </p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* 4 · tabela de licenças                                                      */
/* -------------------------------------------------------------------------- */

/**
 * A amostra de linhas de licença, com o rodapé do **agregado** —
 * `showsSampling` no default, porque este card consome `bom_totals.lines`.
 */
function LicenseTableCard({ sections, snapshots }: DiagnosticTabProps) {
  const { t } = useTranslation();
  const lines = licenseLines(sections);
  const totals = licenseTotals(sections);
  // #682: o desenho aprovado põe os dois cenários como colunas **desta**
  // tabela, e não num card à parte. Quem apresenta lê a mudança na própria
  // linha (`46 → 45`) em vez de correlacionar dois lugares.
  const composition = licensingComposition(sections);

  return (
    <Section
      sections={sections}
      name="bom_totals"
      title={t("Licensing composition")}
      snapshots={snapshots}
    >
      <p className="mb-3 max-w-[100ch] text-[12.5px] leading-relaxed text-[#596b85]">
        {t(
          "GROW model: per user, per SKU, with minimum blocks. Each named user enters one SKU; the billable count respects the line's minimum block. The optimization reclassifies users by the actual use measured in Act 1 and handles lines below the minimum block.",
        )}
      </p>
      {lines.length > 0 ? (
        <>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("SKU")}</TableHead>
                  <TableHead className="text-right">{t("Users")}</TableHead>
                  <TableHead className="text-right">{t("Billable")}</TableHead>
                  {composition.hasScenario ? (
                    <>
                      <TableHead className="text-right">
                        {t("R$/month list")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("R$/month optimized")}
                      </TableHead>
                      <TableHead className="text-right">{t("Delta")}</TableHead>
                    </>
                  ) : (
                    <>
                      <TableHead className="text-right">
                        {t("Unit price")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("Monthly")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("Annual")}
                      </TableHead>
                    </>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((line, index) => (
                  <TableRow
                    key={`${line.skuNumber ?? "unknown"}-${index}`}
                    data-testid={`license-row-${line.skuNumber ?? index}`}
                  >
                    <TableCell className="align-top">
                      <b className="font-mono text-xs text-[#08254c]">
                        {line.skuNumber ?? NO_VALUE}
                      </b>
                      {line.description && (
                        <div className="text-[11px] text-[#7b8799]">
                          {line.description}
                        </div>
                      )}
                      {line.isDigitalAccess && (
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          <Pill tone="navy">{t("Digital Access")}</Pill>
                          {/*
                            O rótulo que a SAP dá ao número (#685). Com origem
                            ECC não existe a medição embutida que ela considera
                            relevante para auditoria, e o que sobra é o serviço
                            de avaliação, que a própria SAP descreve como "only
                            an estimation" e que "will lean higher versus
                            lower". Sem esta pílula o Ato 5 venderia estimativa
                            como medição.
                          */}
                          {line.basis === "estimate" && (
                            <span data-testid="digital-access-estimate">
                              <Pill tone="gold">{t("Estimated figure")}</Pill>
                            </span>
                          )}
                        </div>
                      )}
                      {line.isDigitalAccess && line.basis === "estimate" && (
                        <div
                          data-testid="digital-access-basis-note"
                          className="mt-1 text-[10px] leading-snug text-[#7b8799]"
                        >
                          {/*
                            #944: a base é o último ano completo *com fluxo*. Num
                            sistema cujo movimento parou antes — demonstração, ou
                            ECC congelado com a operação já em outro lugar — o
                            último ano completo está vazio e a extração recua.
                            Afirmar "last full year" ali seria falso, e é o tipo
                            de frase que ninguém confere numa proposta.
                          */}
                          {line.basisYearIsFallback && line.basisYear
                            ? t(
                                "Estimated from the document flow of {{year}}, the last year with movement: {{empty}} has no documents. SAP treats the pre-migration figure as not relevant for audits.",
                                {
                                  year: line.basisYear,
                                  empty: line.lastCompleteYear,
                                },
                              )
                            : t(
                                "Estimated from the document flow of the last full year. SAP treats the pre-migration figure as not relevant for audits.",
                              )}
                        </div>
                      )}
                      {/*
                        #937: a franquia de 20.000 documentos por usuário
                        qualificado, do material da SAP. Ela costuma cobrir o
                        consumo inteiro num cliente médio, e a linha então vale
                        zero. Sem esta nota, esse zero fica igual ao zero de
                        quem não gera documento nenhum.
                      */}
                      {line.isDigitalAccess &&
                        line.entitlement != null &&
                        line.entitlement > 0 && (
                          <div
                            data-testid="digital-access-entitlement-note"
                            className="mt-1 text-[10px] leading-snug text-[#7b8799]"
                          >
                            {t(
                              "{{used}} weighted documents against a {{allowance}}/year allowance from the licensed users.",
                              {
                                used: formatCount(line.totalWeighted ?? 0),
                                allowance: formatCount(line.entitlement),
                              },
                            )}
                          </div>
                        )}
                    </TableCell>
                    {/* Uma contagem só: o cenário não publica gente do lado
                        da lista, e o mecanismo de seta prometia uma comparação
                        que nunca existiu. A seta do desenho é a do faturável. */}
                    <TableCell className="align-top text-right tabular-nums">
                      {composition.rows[index]?.users ?? NO_VALUE}
                    </TableCell>
                    <TableCell className="align-top text-right tabular-nums">
                      <Transition
                        from={composition.rows[index]?.listBillable}
                        to={line.effectiveCount}
                        show={composition.hasScenario}
                      />
                      {/* A unidade, uma vez só: quando há mínimo, a linha
                          abaixo já diz "blocos" e o rótulo solto empilhava um
                          terceiro andar com a palavra repetida. */}
                      {line.isDigitalAccess && line.minBlocks == null && (
                        <div className="text-[11px] text-[#7b8799]">
                          {t("blocks")}
                        </div>
                      )}
                      {line.minBlocks != null && (
                        <div className="text-[11px] text-[#a3adbb]">
                          {t("min {{min}} blocks", { min: line.minBlocks })}
                        </div>
                      )}
                    </TableCell>
                    {composition.hasScenario ? (
                      <>
                        <LicensePriceCell
                          value={composition.rows[index]?.listMonthly}
                          included={includedInAllowance(line)}
                        />
                        <LicensePriceCell
                          value={line.monthlyTotal}
                          included={includedInAllowance(line)}
                          emphasis
                        />
                        <DeltaCell
                          value={composition.rows[index]?.deltaMonthly}
                        />
                      </>
                    ) : (
                      <>
                        <LicensePriceCell
                          value={line.unitPriceBrl}
                          included={includedInAllowance(line)}
                        />
                        <LicensePriceCell
                          value={line.monthlyTotal}
                          included={includedInAllowance(line)}
                        />
                        <LicensePriceCell
                          value={line.annualTotal}
                          included={includedInAllowance(line)}
                        />
                      </>
                    )}
                  </TableRow>
                ))}
                {/*
                  A linha que só existe do lado da lista: o SKU que a otimização
                  zerou não tem linha de BOM, então ela não tem descrição, preço
                  unitário nem mínimo. Sem ela o custo de lista sumia do Total e
                  o card se contradizia com a faixa de economia (#682).
                */}
                {composition.rows
                  .filter((row) => !row.fromBom && row.coveredByAllowance)
                  .map((row) => (
                    <TableRow
                      key={row.sku}
                      data-testid={`license-row-${row.sku}`}
                    >
                      <TableCell className="align-top">
                        <b className="font-mono text-xs text-[#08254c]">
                          {row.sku}
                        </b>
                        <div className="text-[11px] text-[#7b8799]">
                          {t("Developers included at no cost with SAP Finance Base")}
                        </div>
                      </TableCell>
                      <TableCell className="align-top text-right tabular-nums">
                        {row.users ?? NO_VALUE}
                      </TableCell>
                      <TableCell className="align-top text-right tabular-nums">
                        {row.optimizedBillable ?? NO_VALUE}
                      </TableCell>
                      <LicensePriceCell value={undefined} included />
                      <LicensePriceCell value={undefined} included />
                      <TableCell className="align-top text-right tabular-nums">
                        {NO_VALUE}
                      </TableCell>
                    </TableRow>
                  ))}
                {composition.rows
                  .filter((row) => !row.fromBom && !row.coveredByAllowance)
                  .map((row) => (
                    <TableRow
                      key={row.sku}
                      data-testid={`license-row-${row.sku}`}
                    >
                      <TableCell className="align-top">
                        <b className="font-mono text-xs text-[#08254c]">
                          {row.sku}
                        </b>
                        <div className="text-[11px] text-[#7b8799]">
                          {t("Not billed in the optimized arrangement")}
                        </div>
                      </TableCell>
                      <TableCell className="align-top text-right tabular-nums">
                        {row.users ?? NO_VALUE}
                      </TableCell>
                      <TableCell className="align-top text-right tabular-nums">
                        <Transition
                          from={row.listBillable}
                          to={row.optimizedBillable}
                          show
                        />
                      </TableCell>
                      <LicensePriceCell value={row.listMonthly} />
                      <LicensePriceCell value={row.optimizedMonthly} />
                      <DeltaCell value={row.deltaMonthly} />
                    </TableRow>
                  ))}
                {composition.hasScenario && (
                  <TotalRow composition={composition} />
                )}
              </TableBody>
            </Table>
          </div>
          {composition.hasScenario && <SavingsBand composition={composition} />}
          {totals && <LicenseFooter totals={totals} />}
        </>
      ) : (
        <p className="text-xs text-[#7b8799]">
          {t("No licence line in this run.")}
        </p>
      )}
    </Section>
  );
}

function LicensePriceCell({
  value,
  included = false,
  emphasis = false,
}: {
  value: number | undefined;
  /** Digital Access dentro da franquia: sem preço porque não há o que comprar. */
  included?: boolean;
  /** O desenho põe a coluna otimizada em semibold: é o número que fica. */
  emphasis?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <TableCell
      className={cn(
        "align-top text-right tabular-nums",
        emphasis && "font-semibold",
      )}
    >
      {value == null ? (
        <span className="text-xs font-medium text-[#596b85]">
          {included ? t("Included in the allowance") : t("To quote")}
        </span>
      ) : (
        formatBrl(value)
      )}
    </TableCell>
  );
}

function LicenseFooter({
  totals,
}: {
  totals: {
    monthly: number | undefined;
    annual: number | undefined;
    unpricedCount: number;
    unpricedUnknownCount: number;
  };
}) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="license-price-total"
      className="mt-3 flex flex-wrap items-baseline justify-between gap-2 rounded-md bg-[#f7f9fc] px-3 py-2 text-xs text-[#596b85]"
    >
      <span>
        {t("Monthly")}
        <b className="ml-2 font-semibold tabular-nums text-[#08254c]">
          {totals.monthly == null ? NO_VALUE : formatBrl(totals.monthly)}
        </b>
        <span className="mx-2 text-[#a3adbb]">·</span>
        {t("Annual")}
        <b className="ml-2 font-semibold tabular-nums text-[#08254c]">
          {totals.annual == null ? NO_VALUE : formatBrl(totals.annual)}
        </b>
      </span>
      {totals.unpricedCount > 0 && (
        <span className="text-[#7b8799]">
          {/* "A cotar" é a ação do `on_request`, em que a SAP remete à lista
              completa de preços. Para uma linha que falta na NOSSA referência
              a ação é semear, e mandar cotar desperdiça a ida ao cliente.
              Só troca a copy quando sabemos que é lacuna: linha sem
              `price_status` é payload antigo e segue como estava (#668). */}
          {totals.unpricedUnknownCount > 0
            ? t("{{count}} more without a price, not included in the totals", {
                count: totals.unpricedCount,
              })
            : t("{{count}} more to quote, not included in the totals", {
                count: totals.unpricedCount,
              })}
        </span>
      )}
    </div>
  );
}

/**
 * A ressalva que fecha a aba: quantos objetos o **catálogo** conta como fora
 * de escopo hoje. Fonte própria (`wricef_catalog`, tabela de projeto) — não
 * depende de outro card, e some sozinha quando a
 * seção está fechada.
 */
/* -------------------------------------------------------------------------- */
/* 3 · lista contra otimizado, dentro da composição                            */
/* -------------------------------------------------------------------------- */

/** `46 → 45`, a transição que o desenho põe em cada linha. */
function Transition({
  from,
  to,
  show,
}: {
  from: number | undefined;
  to: number | undefined;
  show: boolean;
}) {
  if (!show || from == null || from === to) return <>{to ?? NO_VALUE}</>;
  return (
    <span className="whitespace-nowrap">
      {formatCount(from)} <span className="text-[#a3adbb]">→</span>{" "}
      {to ?? NO_VALUE}
    </span>
  );
}

/**
 * O Delta assinado, com as três cores do desenho.
 *
 * Verde economiza, **laranja custa** e o traço cinza é "não mudou". A terceira
 * importa tanto quanto as duas primeiras: linha que não muda com um zero em
 * verde afirma economia onde não houve nada.
 */
function DeltaCell({ value }: { value: number | undefined }) {
  if (value == null || value === 0) {
    return (
      <TableCell className="align-top text-right tabular-nums text-[#7b8799]">
        {NO_VALUE}
      </TableCell>
    );
  }
  const saves = value < 0;
  return (
    <TableCell
      data-testid="delta-cell"
      data-tone={saves ? "saves" : "costs"}
      className={cn(
        "align-top text-right font-semibold tabular-nums",
        saves ? "text-[#27a155]" : "text-[#c25500]",
      )}
    >
      {saves ? "−" : "+"}
      {formatBrl(Math.abs(value)).replace("R$ ", "")}
    </TableCell>
  );
}

/** A linha de Total do desenho, no ocre claro. */
function TotalRow({ composition }: { composition: LicensingComposition }) {
  const { t } = useTranslation();
  return (
    <TableRow data-testid="composition-total" className="bg-[#fdf7e6]">
      <TableCell className="font-bold text-[#08254c]">{t("Total")}</TableCell>
      <TableCell className="text-right font-bold tabular-nums">
        {composition.totalUsers ?? NO_VALUE}
      </TableCell>
      <TableCell className="text-right font-bold tabular-nums">
        <Transition
          from={composition.totalListBillable}
          to={composition.totalOptimizedBillable}
          show
        />
      </TableCell>
      <LicensePriceCell value={composition.totalListMonthly} emphasis />
      <LicensePriceCell value={composition.totalOptimizedMonthly} emphasis />
      <DeltaCell value={composition.totalDeltaMonthly} />
    </TableRow>
  );
}

/**
 * A faixa de economia e a prosa das alavancas, como o desenho as põe.
 *
 * O desenho escreve as três alavancas em **prosa com números**, e não numa
 * segunda tabela: a devolutiva permite explicar a métrica de cada SKU e proíbe
 * abrir a planilha de cálculo, e a frase é o que o vendedor fala em voz alta.
 */
function SavingsBand({ composition }: { composition: LicensingComposition }) {
  const { t } = useTranslation();
  const savings = composition.annualSavings;

  // Economia nula com cenário calculado quer dizer que **parte do arranjo não
  // tem preço**, e o motor anula os dois totais inteiros quando isso acontece —
  // não são totais parciais, são ausentes. Sumir em silêncio deixaria o card
  // com a tabela e sem nenhuma linha de economia, sem dizer por quê.
  if (savings == null) {
    return (
      <div className="mt-3.5">
        <Caveat testId="composition-savings-unpriced" tone="warning">
          {t(
            "Part of the arrangement has no price, so the comparison was not totalled. This is not the same as no savings.",
          )}
        </Caveat>
        <DroppedLeversCaveat composition={composition} />
      </div>
    );
  }

  return (
    <div className="mt-3.5">
      <div className="flex flex-wrap items-center gap-3 text-[13px] font-semibold text-[#08254c]">
        {t("The licensing saving we found:")}
        <span
          data-testid="composition-savings-band"
          className={cn(
            "inline-flex items-center gap-2 rounded-full border px-3.5 py-[5px] text-[12.5px]",
            savings > 0
              ? "border-[#27a15540] bg-[#27a1551a] text-[#27a155]"
              : savings < 0
                ? "border-[#c2550040] bg-[#c255001a] text-[#c25500]"
                : "border-[#e8ebf0] bg-[#f7f9fc] text-[#596b85]",
          )}
        >
          {/*
            Economia negativa quer dizer que o arranjo otimizado sai **mais
            caro** que o de lista, e antes ela caía no galho do zero e afirmava
            "já é o mais barato" — o oposto do que houve. O motor escolhe pelo
            bloco cobrado e mede a economia na capacidade resolvida, então os
            dois podem discordar de sinal. Achado da revisão de 27/09/2026.
          */}
          {savings > 0
            ? t("{{amount}} per year, with the same users served", {
                amount: formatBrl(savings),
              })
            : savings < 0
              ? t(
                  "The optimized arrangement costs {{amount}} more per year than the list one.",
                  {
                    amount: formatBrl(Math.abs(savings)),
                  },
                )
              : t(
                  "The current arrangement is already the cheapest one with the same capacity.",
                )}
        </span>
      </div>
      <LeversProse composition={composition} />
      <DroppedLeversCaveat composition={composition} />
    </div>
  );
}

/**
 * As três alavancas em **prosa**, como o desenho aprovado as escreve.
 *
 * O protótipo não põe as alavancas numa segunda tabela: ele escreve a frase que
 * o vendedor fala em voz alta, com o número de cada uma em negrito. A
 * devolutiva permite explicar a métrica de cada SKU e proíbe abrir a planilha
 * de cálculo, e é essa frase que separa as duas coisas.
 *
 * Só nomeia alavanca que **apareceu nesta execução**: a frase do desenho cita
 * as três porque o cenário dele tem as três, e afirmar uma que não ocorreu
 * seria inventar método.
 */
function LeversProse({ composition }: { composition: LicensingComposition }) {
  const { t } = useTranslation();
  if (composition.levers.length === 0) return null;

  return (
    <p
      data-testid="composition-levers-prose"
      className="mt-3 max-w-[110ch] text-[12.5px] leading-relaxed text-[#596b85]"
    >
      {t("The levers at work:")}{" "}
      {composition.levers.map((lever, index) => (
        <span key={lever.lever}>
          {index > 0 &&
            (index === composition.levers.length - 1 ? ` ${t("and")} ` : ", ")}
          <b className="text-[#08254c]">{leverLabel(lever.lever, t)}</b>{" "}
          <span>{t("({{count}} users)", { count: lever.affectedCount })}</span>
        </span>
      ))}
      .
    </p>
  );
}

/**
 * Quem entrou na conta mas teve a **alavanca** descartada (#753).
 *
 * O agregador publica `levers_dropped` de propósito: são usuários cujo código
 * de origem saiu do catálogo, então a licença deles é real e continua nos dois
 * cenários, mas não há contrafactual a explicar. Sem esta linha eles aparecem
 * como "sem alavanca" e ninguém sabe que houve descarte.
 */
function DroppedLeversCaveat({
  composition,
}: {
  composition: LicensingComposition;
}) {
  const { t } = useTranslation();
  if (!composition.leversDropped) return null;

  return (
    <Caveat testId="composition-levers-dropped" tone="warning">
      {t(
        "{{count}} user(s) kept their licence but have no explained lever — their origin code is not in the active catalogue.",
        { count: composition.leversDropped },
      )}
    </Caveat>
  );
}

/* -------------------------------------------------------------------------- */
/* 5 · de onde vem cada número: métrica e consumo por SKU                      */
/* -------------------------------------------------------------------------- */

/**
 * As duas famílias de métrica do BOM: por usuário e por consumo (#682).
 *
 * O desenho aprovado tem três abas. Só a primeira tem medição hoje, e as outras
 * duas ficam **no layout**, dizendo qual parâmetro falta — e não um "não
 * coletado" genérico. A diferença importa: a conversa com quem cura o dado
 * chega com a pergunta exata.
 *
 * Investigado contra a fonte da SAP em 26/09/2026:
 *
 * - **Additional Messages**: 1 mensagem = 1 transmissão de até 250 KB, com o
 *   excedente contado em blocos de 250 KB, e pacotes adicionais vendidos em
 *   blocos de 10.000/mês. O inbound é derivável da volumetria de documentos que
 *   já medimos; faltam a razão de outbound e os pacotes contratados.
 * - **SAP BTP**: o CPEA é *commit-to-consume* e o crédito é consumido por uso
 *   real de serviço, reportado pela SAP em balance statement. O driver existe
 *   (as extensões side-by-side que o Architect conta); faltam a taxa de
 *   créditos por extensão e os créditos contratados.
 *
 * Nenhum dos dois precisa de extração nova: precisam de curadoria, no mesmo
 * molde dos dias-base do esforço.
 */
function MetricSourceCard({ sections, snapshots }: DiagnosticTabProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<"digital" | "messages" | "btp">("digital");
  const digital = digitalAccessConsumption(sections);

  return (
    <Section
      sections={sections}
      name="bom_totals"
      title={t("Where each number comes from: metric and consumption per SKU")}
      snapshots={snapshots}
      showsSampling={false}
    >
      <p className="mb-3 text-[12px] text-[#7b8799]">
        {t(
          "Two metric families in the BOM: per user (table above) and per consumption (below, estimated from the Act 1 volumes)",
        )}
      </p>

      <div className="mb-4 inline-flex items-center gap-1.5 rounded-lg border border-[#e8ebf0] bg-white p-1">
        <MetricTab
          id="digital"
          active={tab}
          onSelect={setTab}
          label={t("Digital Access")}
        />
        <MetricTab
          id="messages"
          active={tab}
          onSelect={setTab}
          label={t("Additional Messages")}
        />
        <MetricTab
          id="btp"
          active={tab}
          onSelect={setTab}
          label={t("SAP BTP")}
        />
      </div>

      {tab === "digital" && <DigitalAccessPanel consumption={digital} />}
      {tab === "messages" && (
        <MissingParameterPanel
          testId="metric-panel-messages"
          method={t(
            "One message is one transmission of up to 250 KB processed by Integration Suite; anything above that counts in further 250 KB blocks, and extra packages are sold in blocks of 10,000 messages a month.",
          )}
          have={t(
            "Inbound is derivable from the document volumes measured in Act 1.",
          )}
          missing={[
            t(
              "the outbound ratio, which is an assumption agreed with the customer",
            ),
            t("how many additional message packages are contracted"),
          ]}
        />
      )}
      {tab === "btp" && (
        <MissingParameterPanel
          testId="metric-panel-btp"
          method={t(
            "CPEA is commit-to-consume: credits are spent by actual service usage — extension runtime, connectivity, events — and SAP reports it in a monthly balance statement.",
          )}
          have={t(
            "The driver is measured: the Architect counts the side-by-side extensions the target requires.",
          )}
          missing={[
            t("how many credits one side-by-side extension consumes"),
            t("how many CPEA credits are contracted"),
          ]}
        />
      )}
    </Section>
  );
}

/**
 * Nome legível de cada tipo de documento do Digital Access.
 *
 * As nove chaves são as do `DA_DOC_WEIGHTS`. Texto fixo por `case`, e não mapa
 * indexado, pelo mesmo motivo do `leverLabel`: o guarda de i18n não enxerga
 * chamada de tradução com variável, e a tabela saía com `sales` e `purchase`
 * crus numa tela em português.
 */
function documentTypeLabel(type: string, t: (key: string) => string): string {
  switch (type) {
    case "sales":
      return t("Sales");
    case "purchase":
      return t("Purchase");
    case "invoice":
      return t("Invoice");
    case "manufacturing":
      return t("Manufacturing");
    case "quality":
      return t("Quality");
    case "service_maintenance":
      return t("Service and maintenance");
    case "time_management":
      return t("Time management");
    case "financial":
      return t("Financial");
    case "material":
      return t("Material");
    default:
      return type;
  }
}

function MetricTab({
  id,
  active,
  onSelect,
  label,
}: {
  id: "digital" | "messages" | "btp";
  active: string;
  onSelect: (id: "digital" | "messages" | "btp") => void;
  label: string;
}) {
  const on = active === id;
  return (
    <button
      type="button"
      data-testid={`metric-tab-${id}`}
      data-active={on}
      onClick={() => onSelect(id)}
      className={cn(
        "flex h-8 items-center rounded-md px-3.5 font-mono text-[11px] uppercase tracking-[0.1em]",
        on ? "bg-[#08254c] text-white" : "bg-white text-[#596b85]",
      )}
    >
      {label}
    </button>
  );
}

/** Percentual com uma casa, que é como o desenho escreve (`20,1%`). O
 * separador é o do idioma, então `formatCount` e não template literal. */
function formatPercent(share: number): string {
  return `${formatCount(share * 100, { decimals: 1 })}%`;
}

/**
 * A aba que tem medição: franquia contra consumo, com a quebra por tipo.
 *
 * O desenho quebra por **integração**; a medição não tem esse eixo, porque
 * ninguém extrai qual interface cria qual documento. O tipo é o que se mede, e
 * o peso da SAP entra na coluna para a soma ponderada ser legível.
 */
function DigitalAccessPanel({
  consumption,
}: {
  consumption: MetricConsumption | undefined;
}) {
  const { t } = useTranslation();
  if (!consumption) {
    return (
      <p
        data-testid="metric-panel-digital-absent"
        className="text-[13px] text-[#596b85]"
      >
        {t(
          "This run has no Digital Access line — the origin does not expose the document flow.",
        )}
      </p>
    );
  }

  const { entitlement, consumed, balance, usedShare } = consumption;

  return (
    <div data-testid="metric-panel-digital">
      {consumption.countsDerivedDocuments && (
        // #948: a contagem é teto, e o painel diz por quê. O aviso sai da
        // declaração do cálculo, não de uma regra guardada aqui: no dia em
        // que a exclusão existir, ele some sozinho.
        <p
          data-testid="digital-access-derived-note"
          className="mb-3 text-[12px] leading-snug text-[#7b8799]"
        >
          {t(
            "This count is an upper bound. The contract does not count a document that another document creates — a sales order that generates a delivery, an invoice, a financial and a material document counts as one — and separating them needs document traceability that the extraction does not read yet.",
          )}
        </p>
      )}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,320px)_1fr]">
        <EntitlementPanel consumption={consumption} />

        <div>
          {consumption.byType.length > 0 ? (
            <Table data-testid="digital-breakdown">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("Document type")}</TableHead>
                  <TableHead className="text-right">
                    {t("Created in the ERP")}
                  </TableHead>
                  <TableHead className="text-right">{t("Weight")}</TableHead>
                  <TableHead className="text-right">{t("Weighted")}</TableHead>
                  <TableHead className="text-right">
                    {t("% licensed")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {consumption.byType.map((row) => (
                  <TableRow
                    key={row.type}
                    data-testid={`digital-type-${row.type}`}
                  >
                    <TableCell>{documentTypeLabel(row.type, t)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCount(row.count)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-[#7b8799]">
                      ×{formatCount(row.weight, { decimals: 1 })}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {formatCount(row.weighted)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-[#596b85]">
                      {row.share == null ? NO_VALUE : formatPercent(row.share)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow
                  data-testid="digital-breakdown-total"
                  className="bg-[#fdf7e6]"
                >
                  <TableCell className="font-bold text-[#08254c]">
                    {t("Total · estimated consumption")}
                  </TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell className="text-right font-bold tabular-nums">
                    {consumed == null ? NO_VALUE : formatCount(consumed)}
                  </TableCell>
                  <TableCell className="text-right font-bold tabular-nums">
                    {usedShare == null ? NO_VALUE : formatPercent(usedShare)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          ) : (
            <p
              data-testid="digital-breakdown-absent"
              className="text-[13px] text-[#596b85]"
            >
              {t(
                "This run does not carry the breakdown by document type — only the total it consumed.",
              )}
            </p>
          )}

          {usedShare != null && (
            <div className="mt-4">
              <p className={cn(OVERLINE, "mb-1.5")}>
                {t("Estimated consumption against licensed")}
              </p>
              <div className="h-2 overflow-hidden rounded-full bg-[#eff2f6]">
                <div
                  data-testid="digital-usage-bar"
                  className="h-full bg-[#ffc83d]"
                  style={{ width: `${Math.min(usedShare, 1) * 100}%` }}
                />
              </div>
              <div className="mt-1.5 flex flex-wrap items-baseline justify-between gap-2 text-[11px]">
                <span
                  data-testid="digital-usage-label"
                  className="font-semibold text-[#16223a]"
                >
                  {t("Consumption: {{docs}} docs ({{percent}})", {
                    docs: consumed == null ? NO_VALUE : formatCount(consumed),
                    percent: formatPercent(usedShare),
                  })}
                </span>
                <span
                  data-testid="digital-balance-label"
                  className="text-[#7b8799]"
                >
                  {t("Balance: {{docs}} docs ({{percent}})", {
                    docs: balance == null ? NO_VALUE : formatCount(balance),
                    percent:
                      entitlement != null && entitlement > 0 && balance != null
                        ? formatPercent(balance / entitlement)
                        : NO_VALUE,
                  })}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <p className="mt-3 text-[11px] leading-snug text-[#7b8799]">
        {t(
          "The breakdown is by document type, not by integration: nobody extracts which interface creates which document.",
        )}{" "}
        {consumption.isEstimate &&
          t(
            "Consumption figures are an estimate, not a measurement on the target.",
          )}
      </p>
    </div>
  );
}

/**
 * A franquia, escrita como a soma das suas parcelas.
 *
 * O desenho põe isto num painel escuro à esquerda, e a razão é de conteúdo e
 * não de estilo: "2.720.000 documentos" sozinho não se confere. "FIN Base · 45
 * usuários × 20.000 docs" se discute na reunião, que é onde o número vai ser
 * contestado.
 */
function EntitlementPanel({ consumption }: { consumption: MetricConsumption }) {
  const { t } = useTranslation();

  return (
    <div
      data-testid="digital-entitlement-panel"
      className="rounded-lg bg-[#08254c] p-4"
    >
      <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-[#ffc83d]">
        {t("Digital Access · licensed")}
      </p>

      {consumption.sources.length > 0 ? (
        <ul className="mt-3 space-y-2">
          {consumption.sources.map((source) => (
            <li
              // A chave cai no mesmo laço do `data-testid`: SKU sem catálogo tem
              // descrição vazia, e duas linhas assim colidiriam em `undefined`.
              key={source.sku ?? source.description ?? "sem-sku"}
              data-testid={`digital-source-${source.sku ?? "sem-sku"}`}
              className="flex items-baseline justify-between gap-3 text-[12px] text-[#c9d4e4]"
            >
              <span>
                {source.description ?? source.sku} ·{" "}
                {t("{{users}} users × {{docs}} docs", {
                  users: formatCount(source.users),
                  docs: formatCount(source.docsPerUser),
                })}
              </span>
              <span className="shrink-0 font-semibold tabular-nums text-[#ffc83d]">
                {formatCount(source.docs)}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p
          data-testid="digital-sources-absent"
          className="mt-3 text-[12px] text-[#c9d4e4]"
        >
          {t("No SKU in this BOM grants a document allowance.")}
        </p>
      )}

      <p className="mt-4 text-[11px] text-[#8fa2bd]">
        {t("Total licensed / year")}
      </p>
      <p
        data-testid="digital-entitlement"
        className="text-[26px] font-bold leading-tight tabular-nums text-white"
      >
        {consumption.entitlement == null
          ? NO_VALUE
          : t("{{docs}} docs", { docs: formatCount(consumption.entitlement) })}
      </p>

      <p className="mt-3 text-[11px] leading-snug text-[#8fa2bd]">
        {t(
          "Document-based model: it counts the document created in the ERP by indirect access. Financial and Material Documents count ×0.2; every other type counts ×1.0.",
        )}
      </p>
    </div>
  );
}

/**
 * A aba cuja métrica é conhecida e cujo **parâmetro** falta.
 *
 * Diferente de "não coletado": o método está investigado e escrito, o que temos
 * está nomeado, e o que falta é uma lista de perguntas. Quem for curar o dado
 * lê daqui o que precisa decidir.
 */
function MissingParameterPanel({
  testId,
  method,
  have,
  missing,
}: {
  testId: string;
  method: string;
  have: string;
  missing: string[];
}) {
  const { t } = useTranslation();
  return (
    <div
      data-testid={testId}
      className="text-[12.5px] leading-relaxed text-[#596b85]"
    >
      <Caveat testId={`${testId}-caveat`} tone="warning">
        {t("Not calculated yet: the metric is known, the parameter is not.")}
      </Caveat>
      <p className="mb-2">
        <b className="text-[#08254c]">{t("How SAP counts it:")}</b> {method}
      </p>
      <p className="mb-2">
        <b className="text-[#08254c]">{t("What we already measure:")}</b> {have}
      </p>
      <p className="mb-1">
        <b className="text-[#08254c]">{t("What is missing:")}</b>
      </p>
      <ul className="ml-4 list-disc">
        {missing.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function OutOfScopeCaveat({
  sections,
}: {
  sections: DiagnosticTabProps["sections"];
}) {
  const { t } = useTranslation();
  const outOfScope = outOfScopeExcluded(sections);
  if (!outOfScope) return null;

  return (
    <Caveat testId="investment-out-of-scope-caveat">
      {t(
        "The WRICEF catalog counts {{outOfScope}} of {{total}} objects as out of scope today — they do not add effort-days, and this count may not match exactly what the last run excluded.",
        { outOfScope: outOfScope.outOfScope, total: outOfScope.total },
      )}
    </Caveat>
  );
}
